// Seed: Bunker Hill ore-zone schedule — which section fed the plant each
// production day (optiflow-update-0909.A5 #286). The working grade-recovery
// curve is unchanged; zone identity surfaces on the trail and bottleneck read.

import type { OreZone, OreZoneSchedule } from '@/versions/v1/schema/ore-zone';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { diffMinutes } from '@/versions/v1/shared/clock.derive';

export const ORE_ZONES: readonly OreZone[] = [
  {
    id: 'oz-main',
    label: 'Main stope · 8 level',
    level: '8 level',
    marker: 'circle',
    character: {
      hardness: 'medium',
      agHead: 'design',
      note: 'Primary stope — design Ag head, medium hardness.',
    },
    provenance: '[syn] Main stope feed · medium hardness · design Ag head',
  },
  {
    id: 'oz-ag',
    label: 'High-silver zone · 7 level',
    level: '7 level',
    marker: 'diamond',
    character: {
      hardness: 'hard',
      agHead: 'high',
      note: 'High-silver zone overloads Pb/Ag circuit — needs more residence time.',
    },
    provenance: '[syn] High-silver zone · hard feed · elevated Ag head',
  },
] as const;

/** Production-day feed schedule — `from` inclusive, `to` exclusive. */
export const ORE_ZONE_SCHEDULE: OreZoneSchedule = [
  { zoneId: 'oz-main', from: '2026-05-24T18:00', to: '2026-06-05T18:00' },
  { zoneId: 'oz-ag', from: '2026-06-05T18:00' },
];

const ZONE_BY_ID: Readonly<Record<string, OreZone>> = Object.fromEntries(
  ORE_ZONES.map((zone) => [zone.id, zone]),
);

/** Which ore zone was feeding the plant at `stamp` — undefined before the
 * schedule window opens. */
export function oreZoneAt(stamp: SeriesTime): OreZone | undefined {
  for (const entry of ORE_ZONE_SCHEDULE) {
    if (diffMinutes(stamp, entry.from) < 0) continue;
    if (entry.to !== undefined && diffMinutes(stamp, entry.to) >= 0) continue;
    return ZONE_BY_ID[entry.zoneId];
  }
  return undefined;
}

/** True when the zone's hardness explains the grinding bottleneck. */
export function isHardOreZone(zone: OreZone): boolean {
  return zone.character.hardness === 'hard';
}
